import { useState, useEffect, useCallback, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";

function fmt(n)     { return (n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }); }
function fmtDate(d) { return d ? new Date(d).toLocaleDateString("fr-FR") : "—"; }
function fmtAgo(d)  {
  if (!d) return "jamais";
  const days = Math.floor((Date.now() - new Date(d)) / 86400000);
  if (days === 0) return "aujourd'hui";
  if (days === 1) return "hier";
  return `il y a ${days}j`;
}

const STAGES       = ["découverte","qualification","proposition","négociation","gagné","perdu"];
const STAGE_COLOR  = { découverte:"#64748b", qualification:"#185fa5", proposition:"#ba7517", négociation:"#8b5cf6", gagné:"#1a9e72", perdu:"#e24b4a" };
const STATUS_COLOR = { nouveau:"#64748b", contacté:"#185fa5", qualifié:"#ba7517", proposition:"#8b5cf6", gagné:"#1a9e72", perdu:"#e24b4a" };
const TYPE_ICONS   = { prospect:"🔍", client:"✅", fournisseur:"🏭", partenaire:"🤝" };
const ACT_ICONS    = { appel:"📞", email:"📧", rdv:"📅", demo:"🖥", relance:"📨", tâche:"✅" };
const ACT_COLOR    = { appel:"#1a9e72", email:"#185fa5", rdv:"#ba7517", demo:"#8b5cf6", relance:"#e24b4a", tâche:"#64748b" };
const BAR_COLORS   = ["#6366f1","#1a9e72","#ba7517","#e24b4a","#8b5cf6","#185fa5"];

// ── Notification Bell ─────────────────────────────────────────────
function NotifBell({ currentCompanyId }) {
  const [data, setData]       = useState({ notifications: [], unread: 0 });
  const [open, setOpen]       = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.crm.notifications().then(setData).catch(() => {});
    const iv = setInterval(() => api.crm.notifications().then(setData).catch(() => {}), 30000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const markAll = async () => {
    await api.crm.markAllNotifsRead().catch(() => {});
    setData(d => ({ ...d, unread: 0, notifications: d.notifications.map(n => ({ ...n, is_read: 1 })) }));
  };

  const markOne = async (id) => {
    await api.crm.markNotifRead(id).catch(() => {});
    setData(d => ({
      ...d,
      unread: Math.max(0, d.unread - 1),
      notifications: d.notifications.map(n => n.id === id ? { ...n, is_read: 1 } : n),
    }));
  };

  const typeIcon = { assignment:"🤝", reassignment:"🔄", new_prospect:"🔍", inactivity:"⚠️" };

  return (
    <div ref={ref} style={{ position:"relative", display:"inline-block" }}>
      <button onClick={() => setOpen(o => !o)} style={{ position:"relative", background:"none", border:"none", cursor:"pointer", fontSize:20, padding:"4px 8px" }}>
        🔔
        {data.unread > 0 && (
          <span style={{ position:"absolute", top:0, right:0, background:"#e24b4a", color:"#fff", borderRadius:"50%", fontSize:9, width:16, height:16, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700 }}>{data.unread > 9 ? "9+" : data.unread}</span>
        )}
      </button>
      {open && (
        <div style={{ position:"absolute", right:0, top:"100%", zIndex:200, background:"var(--surface,#fff)", border:"1px solid var(--border-color,#e2e8f0)", borderRadius:10, boxShadow:"0 8px 24px rgba(0,0,0,.12)", minWidth:340, maxHeight:400, overflowY:"auto" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", borderBottom:"1px solid var(--border-color,#e2e8f0)" }}>
            <span style={{ fontWeight:700, fontSize:13 }}>Notifications ({data.unread} non lues)</span>
            {data.unread > 0 && <button className="btn sm" onClick={markAll} style={{ fontSize:11 }}>Tout lire</button>}
          </div>
          {data.notifications.length === 0 && <div style={{ padding:20, textAlign:"center", color:"var(--text-muted)", fontSize:12 }}>Aucune notification</div>}
          {data.notifications.map(n => (
            <div key={n.id} onClick={() => markOne(n.id)} style={{ display:"flex", gap:10, padding:"10px 14px", borderBottom:"1px solid var(--border-color,#e2e8f0)", background:n.is_read ? "transparent" : "#6366f108", cursor:"pointer" }}>
              <span style={{ fontSize:18, flexShrink:0 }}>{typeIcon[n.type] || "📌"}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight: n.is_read ? 500 : 700 }}>{n.title}</div>
                {n.message && <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:2 }}>{n.message}</div>}
                <div style={{ fontSize:10, color:"var(--text-muted)", marginTop:2 }}>{fmtDate(n.created_at)}</div>
              </div>
              {!n.is_read && <div style={{ width:8, height:8, borderRadius:"50%", background:"#6366f1", flexShrink:0, alignSelf:"center" }} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Onglet Dashboard ──────────────────────────────────────────────
function DashboardCRM({ currentCompanyId, user }) {
  const [stats, setStats]     = useState(null);
  const [today, setToday]     = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const isManager = user?.role === 'admin' || user?.pwa_role === 'manager';

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    api.crm.stats(currentCompanyId).then(setStats).catch(console.error);
    api.crm.todayActivities(currentCompanyId).then(setToday).catch(console.error);
    if (isManager) api.crm.unassigned(currentCompanyId).then(setUnassigned).catch(() => {});
  }, [currentCompanyId, isManager]);

  useEffect(() => { load(); }, [load]);

  if (!stats) return <div style={{ textAlign:"center", padding:40, color:"var(--text-muted)" }}>Chargement…</div>;
  const { contacts, pipeline } = stats;

  return (
    <div>
      <div className="kpi-grid k4">
        <div className="kpi"><div className="kpi-label">Prospects actifs</div><div className="kpi-value">{contacts.prospects}</div></div>
        <div className="kpi"><div className="kpi-label">Pipeline pondéré</div><div className="kpi-value" style={{ color:"var(--g)" }}>{fmt(pipeline.weighted_value)} €</div></div>
        <div className="kpi"><div className="kpi-label">Taux de conversion</div><div className="kpi-value">{stats.convRate}%</div></div>
        <div className="kpi"><div className="kpi-label">Gagnés ce mois</div><div className="kpi-value" style={{ color:"var(--g)" }}>{fmt(pipeline.won_value)} €</div><div className="kpi-sub up">{pipeline.won} deals</div></div>
      </div>

      {stats.staleDeals > 0 && (
        <div style={{ padding:"10px 14px", background:"#e24b4a10", border:"1px solid #e24b4a30", borderRadius:8, marginBottom:12, fontSize:12, color:"#e24b4a", display:"flex", alignItems:"center", gap:8 }}>
          ⚠️ <strong>{stats.staleDeals} deal(s)</strong> sans activité depuis plus de 7 jours
        </div>
      )}

      {isManager && unassigned.length > 0 && (
        <div style={{ padding:"10px 14px", background:"#ba751710", border:"1px solid #ba751730", borderRadius:8, marginBottom:16, fontSize:12, color:"#ba7517", display:"flex", alignItems:"center", gap:8 }}>
          📋 <strong>{unassigned.length} contact(s)</strong> sans commercial assigné — <a href="#" onClick={e => { e.preventDefault(); }} style={{ color:"#ba7517", fontWeight:700, textDecoration:"underline" }}>Voir l'onglet Équipe</a>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <div className="card">
          <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Pipeline par étape</div>
          {STAGES.filter(s => s !== 'perdu').map(s => (
            <div key={s} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <div style={{ width:10, height:10, borderRadius:"50%", background:STAGE_COLOR[s], flexShrink:0 }} />
              <span style={{ flex:1, fontSize:12, textTransform:"capitalize" }}>{s}</span>
              <span style={{ fontSize:12, fontWeight:600, color:STAGE_COLOR[s] }}>{fmt(0)} €</span>
            </div>
          ))}
        </div>

        <div className="card">
          <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Agenda du jour — {today.length} activité(s)</div>
          {today.length === 0 ? (
            <div style={{ textAlign:"center", padding:20, color:"var(--text-muted)", fontSize:12 }}>Aucune activité prévue aujourd'hui</div>
          ) : today.slice(0, 5).map(a => (
            <div key={a.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:"var(--border)" }}>
              <span style={{ fontSize:18 }}>{ACT_ICONS[a.type]}</span>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:600 }}>{a.subject}</div>
                <div style={{ fontSize:11, color:"var(--text-muted)" }}>{a.first_name} {a.last_name} — {a.company_name}</div>
              </div>
              <span style={{ fontSize:11, color:ACT_COLOR[a.type], fontWeight:600 }}>{a.date_scheduled ? new Date(a.date_scheduled).toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"}) : ""}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Modal d'affectation + historique ─────────────────────────────
function AssignModal({ contact, commercials, onDone, onClose }) {
  const [commId, setCommId] = useState(contact.commercial_id || "");
  const [reason, setReason] = useState("");
  const [history, setHistory] = useState([]);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    api.crm.contactHistory(contact.id).then(setHistory).catch(() => {});
  }, [contact.id]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    await api.crm.assignContact(contact.id, { commercial_id: commId || null, reason }).catch(console.error);
    setSaving(false); onDone();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:520 }}>
        <div className="modal-header">
          <h3 className="modal-title">👤 Affectation — {contact.first_name} {contact.last_name}</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={save}>
          <div className="form-group">
            <label className="form-label">Commercial assigné</label>
            <select className="form-control" value={commId} onChange={e => setCommId(e.target.value)}>
              <option value="">— Aucun (non assigné) —</option>
              {commercials.map(c => <option key={c.id} value={c.id}>{c.name} {c.pwa_role === 'manager' ? '(Manager)' : ''}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Raison du changement <span style={{ color:"var(--text-muted)", fontWeight:400 }}>(optionnel)</span></label>
            <input className="form-control" value={reason} onChange={e => setReason(e.target.value)} placeholder="ex: Réorganisation territoire, départ commercial…" />
          </div>
          <div className="btn-group" style={{ justifyContent:"flex-end", marginBottom:16 }}>
            <button type="button" className="btn" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn primary" disabled={saving}>{saving ? "Enregistrement…" : "Enregistrer"}</button>
          </div>
        </form>

        {history.length > 0 && (
          <div>
            <div style={{ fontWeight:700, fontSize:12, marginBottom:8, color:"var(--text-muted)", textTransform:"uppercase" }}>Historique des affectations</div>
            <div style={{ maxHeight:200, overflowY:"auto" }}>
              {history.map(h => (
                <div key={h.id} style={{ display:"flex", gap:10, padding:"8px 0", borderBottom:"1px solid var(--border-color,#e2e8f0)", fontSize:11 }}>
                  <div style={{ flex:1 }}>
                    <span style={{ color:"#e24b4a" }}>{h.old_commercial_name || "Non assigné"}</span>
                    <span style={{ color:"var(--text-muted)", margin:"0 6px" }}>→</span>
                    <span style={{ color:"#1a9e72", fontWeight:600 }}>{h.new_commercial_name || "Non assigné"}</span>
                    {h.reason && <span style={{ color:"var(--text-muted)", marginLeft:8 }}>({h.reason})</span>}
                  </div>
                  <div style={{ color:"var(--text-muted)", flexShrink:0 }}>
                    <div>{h.changed_by}</div>
                    <div>{fmtDate(h.changed_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Onglet Contacts ───────────────────────────────────────────────
function ContactsTab({ currentCompanyId, user }) {
  const [contacts, setContacts]     = useState([]);
  const [total, setTotal]           = useState(0);
  const [search, setSearch]         = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [commFilter, setCommFilter] = useState("");
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [showModal, setShowModal]   = useState(false);
  const [showAssign, setShowAssign] = useState(false);
  const [showActModal, setShowActModal] = useState(false);
  const [form, setForm]             = useState({ last_name:"", first_name:"", company_name:"", job_title:"", email:"", phone:"", source:"autre", status:"nouveau", type:"prospect", commercial_id:"", notes:"" });
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState("");
  const [actForm, setActForm]       = useState({ type:"appel", subject:"", description:"", date_scheduled:"", next_action:"" });
  const [commercials, setCommercials] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [showUnassignedOnly, setShowUnassignedOnly] = useState(false);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [bulkCommId, setBulkCommId] = useState("");
  const [bulkAssigning, setBulkAssigning] = useState(false);

  const isCommercial = user?.pwa_role === 'commercial';
  const isManager    = user?.role === 'admin' || user?.pwa_role === 'manager';

  useEffect(() => {
    api.crm.commercials().then(setCommercials).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    const params = { search, status: statusFilter, type: typeFilter, limit: 100 };
    if (!isCommercial && commFilter) params.commercial_id = commFilter;
    if (showUnassignedOnly) params.unassigned = 'true';
    api.crm.contacts(currentCompanyId, params)
      .then(r => { setContacts(r.contacts || []); setTotal(r.total || 0); })
      .catch(console.error)
      .finally(() => setLoading(false));
    if (isManager) api.crm.unassigned(currentCompanyId)
      .then(rows => setUnassignedCount(Array.isArray(rows) ? rows.length : 0))
      .catch(() => {});
  }, [currentCompanyId, search, statusFilter, typeFilter, commFilter, isCommercial, showUnassignedOnly, isManager]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm({ last_name:"", first_name:"", company_name:"", job_title:"", email:"", phone:"", source:"autre", status:"nouveau", type:"prospect", commercial_id: isCommercial ? String(user.id) : "", notes:"" });
    setError(""); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true); setError("");
    try {
      await api.crm.createContact({ ...form, company_id: currentCompanyId, commercial_id: form.commercial_id || null });
      setShowModal(false); load();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  };

  const addActivity = async (e) => {
    e.preventDefault();
    try {
      await api.crm.createActivity({ ...actForm, contact_id: selected?.id, company_id: currentCompanyId });
      setShowActModal(false); load();
    } catch {}
  };

  return (
    <div>
      <div style={{ display:"flex", gap:8, marginBottom:16, flexWrap:"wrap", alignItems:"center" }}>
        <input className="form-control" placeholder="🔍 Nom, email, société…" style={{ flex:1, minWidth:180 }} value={search} onChange={e => setSearch(e.target.value)} />
        <select className="form-control" style={{ width:"auto" }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="">Tous statuts</option>
          {["nouveau","contacté","qualifié","proposition","gagné","perdu"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="form-control" style={{ width:"auto" }} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">Tous types</option>
          {["prospect","client","fournisseur","partenaire"].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {isManager && (
          <select className="form-control" style={{ width:"auto" }} value={commFilter} onChange={e => setCommFilter(e.target.value)}>
            <option value="">Tous commerciaux</option>
            {commercials.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <div style={{ color:"var(--text-muted)", fontSize:12 }}>{total} contact(s)</div>
        {isManager && (
          <button className="btn" onClick={() => {
            const n = window.prompt("Nom du segment Brevo :");
            if (!n) return;
            const f = {};
            if (statusFilter) f.status = statusFilter;
            if (typeFilter)   f.type   = typeFilter;
            import("../services/api").then(({ default: a }) =>
              a.abonnements.crmSegment({ company_id: currentCompanyId, segment_name: n, filters: f })
                .then(r => alert(`✅ Segment "${n}" créé — ${r.added} contact(s)`))
                .catch(e2 => alert(`❌ ${e2.message}`))
            );
          }}>📧 Segment Brevo</button>
        )}
        {isManager && (
          <button
            className={`btn${showUnassignedOnly ? " primary" : ""}`}
            onClick={() => { setShowUnassignedOnly(v => !v); setCommFilter(""); setSelectedContacts(new Set()); }}
            style={{ position:"relative" }}
          >
            📋 Non assignés
            {unassignedCount > 0 && (
              <span style={{ position:"absolute", top:-5, right:-5, background:"#e24b4a", color:"#fff", borderRadius:"50%", fontSize:9, width:16, height:16, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700 }}>
                {unassignedCount > 9 ? "9+" : unassignedCount}
              </span>
            )}
          </button>
        )}
        <button className="btn primary" onClick={openCreate}>+ Nouveau contact</button>
      </div>

      {isManager && selectedContacts.size > 0 && (
        <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:12, padding:"10px 14px", background:"#6366f110", border:"1px solid #6366f130", borderRadius:8, flexWrap:"wrap" }}>
          <span style={{ fontSize:12, fontWeight:700, color:"#6366f1" }}>{selectedContacts.size} contact(s) sélectionné(s)</span>
          <select className="form-control" style={{ width:190 }} value={bulkCommId} onChange={e => setBulkCommId(e.target.value)}>
            <option value="">— Choisir commercial —</option>
            {commercials.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button
            className="btn primary"
            disabled={!bulkCommId || bulkAssigning}
            onClick={async () => {
              if (!bulkCommId) return;
              setBulkAssigning(true);
              await api.crm.bulkAssign({ contact_ids: [...selectedContacts], commercial_id: Number(bulkCommId) }).catch(console.error);
              setSelectedContacts(new Set()); setBulkCommId(""); setBulkAssigning(false); load();
            }}
          >
            {bulkAssigning ? "Affectation…" : "✓ Assigner en masse"}
          </button>
          <button className="btn" onClick={() => { setSelectedContacts(new Set()); setBulkCommId(""); }}>✕ Annuler</button>
        </div>
      )}

      {isCommercial && (
        <div style={{ padding:"8px 12px", background:"#6366f110", border:"1px solid #6366f130", borderRadius:8, marginBottom:12, fontSize:12, color:"#6366f1" }}>
          💼 Vous voyez uniquement vos contacts assignés
        </div>
      )}

      <div className="card">
        {loading ? (
          <div style={{ textAlign:"center", padding:40, color:"var(--text-muted)" }}>Chargement…</div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                {isManager && (
                  <th style={{ width:32 }}>
                    <input type="checkbox"
                      checked={contacts.length > 0 && contacts.every(c => selectedContacts.has(c.id))}
                      onChange={e => setSelectedContacts(e.target.checked ? new Set(contacts.map(c => c.id)) : new Set())}
                    />
                  </th>
                )}
                <th>Contact</th><th>Type</th><th>Email</th><th>Source</th><th>Statut</th>
                {isManager && <th>Commercial</th>}
                <th>Pipeline</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map(c => (
                <tr key={c.id} style={{ background: selectedContacts.has(c.id) ? "#6366f108" : "transparent" }}>
                  {isManager && (
                    <td>
                      <input type="checkbox"
                        checked={selectedContacts.has(c.id)}
                        onChange={e => {
                          setSelectedContacts(prev => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(c.id); else next.delete(c.id);
                            return next;
                          });
                        }}
                      />
                    </td>
                  )}
                  <td>
                    <div style={{ fontWeight:700 }}>{c.first_name} {c.last_name}</div>
                    <div style={{ fontSize:11, color:"var(--text-muted)" }}>{c.job_title}{c.company_name ? ` · ${c.company_name}` : ""}</div>
                  </td>
                  <td><span style={{ fontSize:11 }}>{TYPE_ICONS[c.type]} {c.type}</span></td>
                  <td style={{ fontSize:12, color:"var(--text-muted)" }}>{c.email || "—"}</td>
                  <td style={{ fontSize:11 }}>{c.source}</td>
                  <td><span style={{ fontSize:11, padding:"2px 8px", borderRadius:8, background:`${STATUS_COLOR[c.status]}18`, color:STATUS_COLOR[c.status], fontWeight:700 }}>{c.status}</span></td>
                  {isManager && (
                    <td>
                      {c.commercial_name
                        ? <span style={{ fontSize:11, fontWeight:600 }}>{c.commercial_name}</span>
                        : <span style={{ fontSize:11, color:"#e24b4a" }}>Non assigné</span>}
                    </td>
                  )}
                  <td style={{ fontSize:12, color:"var(--g)", fontWeight:600 }}>{c.opp_count > 0 ? `${c.opp_count} opp. — ${fmt(c.opp_value)} €` : "—"}</td>
                  <td>
                    <div style={{ display:"flex", gap:4 }}>
                      <button className="btn sm" title="Voir fiche" onClick={() => { setSelected(c); setShowAssign(false); }}>👁</button>
                      {isManager && <button className="btn sm" title="Affecter" onClick={() => { setSelected(c); setShowAssign(true); }}>👤</button>}
                      <button className="btn sm" style={{ fontSize:10 }} onClick={() => { setSelected(c); setActForm({ type:"appel", subject:`Appel ${c.first_name} ${c.last_name}`, description:"", date_scheduled:"", next_action:"" }); setShowActModal(true); }}>+ Acti.</button>
                    </div>
                  </td>
                </tr>
              ))}
              {contacts.length === 0 && <tr><td colSpan={isManager ? 9 : 7} style={{ textAlign:"center", padding:24, color:"var(--text-muted)" }}>Aucun contact</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal création contact */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth:540 }}>
            <div className="modal-header"><h3 className="modal-title">👤 Nouveau contact</h3><button className="modal-close" onClick={() => setShowModal(false)}>✕</button></div>
            {error && <div className="alert danger" style={{ marginBottom:12 }}>{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Prénom</label><input className="form-control" value={form.first_name} onChange={e => setForm(f => ({...f, first_name:e.target.value}))} /></div>
                <div className="form-group"><label className="form-label">Nom *</label><input className="form-control" required value={form.last_name} onChange={e => setForm(f => ({...f, last_name:e.target.value}))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Société</label><input className="form-control" value={form.company_name} onChange={e => setForm(f => ({...f, company_name:e.target.value}))} /></div>
                <div className="form-group"><label className="form-label">Fonction</label><input className="form-control" value={form.job_title} onChange={e => setForm(f => ({...f, job_title:e.target.value}))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" autoComplete="off" value={form.email} onChange={e => setForm(f => ({...f, email:e.target.value}))} /></div>
                <div className="form-group"><label className="form-label">Téléphone</label><input className="form-control" value={form.phone} onChange={e => setForm(f => ({...f, phone:e.target.value}))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Type</label><select className="form-control" value={form.type} onChange={e => setForm(f => ({...f, type:e.target.value}))}>{["prospect","client","fournisseur","partenaire"].map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Source</label><select className="form-control" value={form.source} onChange={e => setForm(f => ({...f, source:e.target.value}))}>{["site web","salon","recommandation","cold email","IA","autre"].map(s => <option key={s} value={s}>{s}</option>)}</select></div>
              </div>
              {isManager && (
                <div className="form-group">
                  <label className="form-label">Commercial assigné</label>
                  <select className="form-control" value={form.commercial_id} onChange={e => setForm(f => ({...f, commercial_id:e.target.value}))}>
                    <option value="">— Non assigné (notifie les managers) —</option>
                    {commercials.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group"><label className="form-label">Notes</label><textarea className="form-control" rows={2} value={form.notes} onChange={e => setForm(f => ({...f, notes:e.target.value}))} /></div>
              <div className="btn-group" style={{ justifyContent:"flex-end", marginTop:8 }}>
                <button type="button" className="btn" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn primary" disabled={saving}>{saving ? "Enregistrement…" : "Créer le contact"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal affectation */}
      {selected && showAssign && (
        <AssignModal
          contact={selected}
          commercials={commercials}
          onDone={() => { setShowAssign(false); setSelected(null); load(); }}
          onClose={() => { setShowAssign(false); setSelected(null); }}
        />
      )}

      {/* Fiche contact */}
      {selected && !showActModal && !showAssign && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div className="modal" style={{ maxWidth:500 }}>
            <div className="modal-header">
              <h3 className="modal-title">{TYPE_ICONS[selected.type]} {selected.first_name} {selected.last_name}</h3>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:16 }}>
              {[["Société", selected.company_name], ["Fonction", selected.job_title], ["Email", selected.email], ["Téléphone", selected.phone], ["Source", selected.source], ["Statut", selected.status], ["Commercial", selected.commercial_name]].map(([l,v]) => v && (
                <div key={l} style={{ background:"#f9fafb", borderRadius:8, padding:"8px 10px" }}>
                  <div style={{ fontSize:10, color:"var(--text-muted)" }}>{l}</div>
                  <div style={{ fontSize:12, fontWeight:600 }}>{v}</div>
                </div>
              ))}
            </div>
            {selected.notes && <div style={{ fontSize:12, color:"var(--text-muted)", marginBottom:12, padding:"8px 10px", background:"#f9fafb", borderRadius:8 }}>{selected.notes}</div>}
            <div className="btn-group" style={{ justifyContent:"flex-end" }}>
              {isManager && <button className="btn" onClick={() => setShowAssign(true)}>👤 Affecter</button>}
              <button className="btn" onClick={() => setSelected(null)}>Fermer</button>
              <button className="btn primary" onClick={() => { setActForm({ type:"appel", subject:`Appel ${selected.first_name} ${selected.last_name}`, description:"", date_scheduled:"", next_action:"" }); setShowActModal(true); }}>+ Activité</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal activité */}
      {showActModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowActModal(false)}>
          <div className="modal" style={{ maxWidth:440 }}>
            <div className="modal-header"><h3 className="modal-title">📋 Nouvelle activité</h3><button className="modal-close" onClick={() => setShowActModal(false)}>✕</button></div>
            <form onSubmit={addActivity}>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Type</label><select className="form-control" value={actForm.type} onChange={e => setActForm(f => ({...f, type:e.target.value}))}>{Object.keys(ACT_ICONS).map(t => <option key={t} value={t}>{ACT_ICONS[t]} {t}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Date prévue</label><input className="form-control" type="datetime-local" value={actForm.date_scheduled} onChange={e => setActForm(f => ({...f, date_scheduled:e.target.value}))} /></div>
              </div>
              <div className="form-group"><label className="form-label">Sujet *</label><input className="form-control" required value={actForm.subject} onChange={e => setActForm(f => ({...f, subject:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Description</label><textarea className="form-control" rows={2} value={actForm.description} onChange={e => setActForm(f => ({...f, description:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Prochaine action</label><input className="form-control" placeholder="ex: Rappel dans 3 jours" value={actForm.next_action} onChange={e => setActForm(f => ({...f, next_action:e.target.value}))} /></div>
              <div className="btn-group" style={{ justifyContent:"flex-end", marginTop:8 }}>
                <button type="button" className="btn" onClick={() => setShowActModal(false)}>Annuler</button>
                <button type="submit" className="btn primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Onglet Pipeline Kanban ────────────────────────────────────────
function PipelineTab({ currentCompanyId }) {
  const [pipeline, setPipeline]     = useState({});
  const [loading, setLoading]       = useState(true);
  const [showOppModal, setShowOppModal] = useState(false);
  const [contacts, setContacts]     = useState([]);
  const [oppForm, setOppForm]       = useState({ contact_id:"", title:"", amount:"", probability:"20", expected_close_date:"", stage:"découverte" });
  const [dragging, setDragging]     = useState(null);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.crm.pipeline(currentCompanyId).then(setPipeline).catch(console.error).finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (currentCompanyId) api.crm.contacts(currentCompanyId, { limit: 200 }).then(r => setContacts(r.contacts || [])).catch(() => {});
  }, [currentCompanyId]);

  const handleDrop = async (stage, oppId) => {
    if (!oppId || !stage) return;
    await api.crm.updateOpp(oppId, { stage }).catch(console.error);
    load();
  };

  const createOpp = async (e) => {
    e.preventDefault();
    await api.crm.createOpp({ ...oppForm, company_id: currentCompanyId, amount: parseFloat(oppForm.amount)||0 }).catch(console.error);
    setShowOppModal(false); load();
  };

  if (loading) return <div style={{ textAlign:"center", padding:40, color:"var(--text-muted)" }}>Chargement…</div>;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:16 }}>
        <button className="btn primary" onClick={() => setShowOppModal(true)}>+ Nouvelle opportunité</button>
      </div>
      <div style={{ display:"flex", gap:10, overflowX:"auto", paddingBottom:8 }}>
        {STAGES.map(stage => {
          const col = pipeline[stage] || { deals:[], total:0, count:0 };
          return (
            <div key={stage} style={{ minWidth:220, flex:"0 0 220px", background:"#f9fafb", borderRadius:10, padding:10, border:`2px solid ${STAGE_COLOR[stage]}22` }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleDrop(stage, dragging); }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                <span style={{ fontSize:12, fontWeight:700, textTransform:"capitalize", color:STAGE_COLOR[stage] }}>{stage}</span>
                <div style={{ textAlign:"right" }}>
                  <span style={{ fontSize:11, fontWeight:700 }}>{col.count}</span>
                  <div style={{ fontSize:10, color:"var(--text-muted)" }}>{fmt(col.total)} €</div>
                </div>
              </div>
              {col.deals.map(d => {
                const stale = d.last_activity && new Date(d.last_activity) < new Date(Date.now() - 7*24*3600000);
                return (
                  <div key={d.id} draggable onDragStart={() => setDragging(d.id)} onDragEnd={() => setDragging(null)}
                    style={{ background:"#fff", borderRadius:8, padding:"10px 10px", marginBottom:8, border:`1px solid ${stale ? "#e24b4a44" : "var(--border-color,#e2e8f0)"}`, cursor:"grab" }}>
                    <div style={{ fontSize:12, fontWeight:700 }}>{d.title}</div>
                    <div style={{ fontSize:11, color:"var(--text-muted)" }}>{d.first_name} {d.last_name}{d.company_name ? ` · ${d.company_name}` : ""}</div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}>
                      <span style={{ fontSize:11, fontWeight:700, color:STAGE_COLOR[stage] }}>{fmt(d.amount)} €</span>
                      <span style={{ fontSize:10, color:"var(--text-muted)" }}>{d.probability}%</span>
                    </div>
                    {stale && <div style={{ fontSize:10, color:"#e24b4a", marginTop:4 }}>⚠️ Inactif 7j+</div>}
                    {d.expected_close_date && <div style={{ fontSize:10, color:"var(--text-muted)" }}>Clôture: {fmtDate(d.expected_close_date)}</div>}
                  </div>
                );
              })}
              {col.deals.length === 0 && <div style={{ textAlign:"center", padding:12, color:"var(--text-muted)", fontSize:11 }}>Glissez des deals ici</div>}
            </div>
          );
        })}
      </div>
      {showOppModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowOppModal(false)}>
          <div className="modal" style={{ maxWidth:460 }}>
            <div className="modal-header"><h3 className="modal-title">💼 Nouvelle opportunité</h3><button className="modal-close" onClick={() => setShowOppModal(false)}>✕</button></div>
            <form onSubmit={createOpp}>
              <div className="form-group"><label className="form-label">Contact *</label><select className="form-control" required value={oppForm.contact_id} onChange={e => setOppForm(f => ({...f, contact_id:e.target.value}))}><option value="">— Choisir —</option>{contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.company_name}</option>)}</select></div>
              <div className="form-group"><label className="form-label">Titre *</label><input className="form-control" required value={oppForm.title} onChange={e => setOppForm(f => ({...f, title:e.target.value}))} /></div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Montant (€)</label><input className="form-control" type="number" min="0" step="100" value={oppForm.amount} onChange={e => setOppForm(f => ({...f, amount:e.target.value}))} /></div>
                <div className="form-group"><label className="form-label">Probabilité (%)</label><input className="form-control" type="number" min="0" max="100" value={oppForm.probability} onChange={e => setOppForm(f => ({...f, probability:e.target.value}))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label className="form-label">Étape</label><select className="form-control" value={oppForm.stage} onChange={e => setOppForm(f => ({...f, stage:e.target.value}))}>{STAGES.map(s => <option key={s} value={s}>{s}</option>)}</select></div>
                <div className="form-group"><label className="form-label">Clôture prévue</label><input className="form-control" type="date" value={oppForm.expected_close_date} onChange={e => setOppForm(f => ({...f, expected_close_date:e.target.value}))} /></div>
              </div>
              <div className="btn-group" style={{ justifyContent:"flex-end", marginTop:8 }}>
                <button type="button" className="btn" onClick={() => setShowOppModal(false)}>Annuler</button>
                <button type="submit" className="btn primary">Créer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Onglet Activités ──────────────────────────────────────────────
function ActivitiesTab({ currentCompanyId }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading]       = useState(true);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.crm.activities(currentCompanyId, { limit: 100 })
      .then(r => setActivities(Array.isArray(r) ? r : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="card">
      {loading ? (
        <div style={{ textAlign:"center", padding:40, color:"var(--text-muted)" }}>Chargement…</div>
      ) : activities.length === 0 ? (
        <div style={{ textAlign:"center", padding:40, color:"var(--text-muted)" }}>Aucune activité enregistrée</div>
      ) : (
        <div>
          {activities.map((a, i) => {
            const showDate = i === 0 || new Date(a.created_at).toDateString() !== new Date(activities[i-1].created_at).toDateString();
            return (
              <div key={a.id}>
                {showDate && <div style={{ fontSize:11, fontWeight:700, color:"var(--text-muted)", padding:"12px 0 6px", textTransform:"uppercase" }}>{new Date(a.created_at).toLocaleDateString("fr-FR",{weekday:"long",day:"numeric",month:"long"})}</div>}
                <div style={{ display:"flex", gap:12, padding:"10px 0", borderBottom:"var(--border)" }}>
                  <div style={{ width:36, height:36, borderRadius:"50%", background:`${ACT_COLOR[a.type]}18`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{ACT_ICONS[a.type]}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:700, fontSize:13 }}>{a.subject}</div>
                    <div style={{ fontSize:12, color:"var(--text-muted)" }}>{a.first_name} {a.last_name}{a.company_name ? ` · ${a.company_name}` : ""}{a.opp_title ? ` — ${a.opp_title}` : ""}</div>
                    {a.description && <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:2 }}>{a.description}</div>}
                    {a.next_action && <div style={{ fontSize:11, color:"#185fa5", marginTop:2 }}>→ {a.next_action}</div>}
                  </div>
                  <div style={{ textAlign:"right", flexShrink:0 }}>
                    <span style={{ fontSize:11, padding:"2px 8px", borderRadius:8, background:`${ACT_COLOR[a.type]}18`, color:ACT_COLOR[a.type], fontWeight:700 }}>{a.type}</span>
                    <div style={{ fontSize:10, color:"var(--text-muted)", marginTop:4 }}>{a.created_by}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Onglet Portefeuille ───────────────────────────────────────────
function PortefeuilleTab({ currentCompanyId, user }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [selectedComm, setSelectedComm] = useState("");
  const [commercials, setCommercials]   = useState([]);
  const [selected, setSelected]   = useState(null);

  const isManager = user?.role === 'admin' || user?.pwa_role === 'manager';
  const commId    = isManager ? selectedComm : (user?.id || "");

  useEffect(() => {
    if (isManager) api.crm.commercials().then(setCommercials).catch(() => {});
  }, [isManager]);

  const load = useCallback(() => {
    if (!currentCompanyId || !commId) { setLoading(false); return; }
    setLoading(true);
    api.crm.portfolio(currentCompanyId, commId)
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId, commId]);

  useEffect(() => { load(); }, [load]);

  if (isManager && !selectedComm) return (
    <div>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontWeight:700, fontSize:14, marginBottom:8 }}>Sélectionnez un commercial pour voir son portefeuille</div>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
          {commercials.map(c => (
            <button key={c.id} className="btn" onClick={() => setSelectedComm(String(c.id))} style={{ padding:"10px 18px" }}>
              <div style={{ fontWeight:700 }}>{c.name}</div>
              <div style={{ fontSize:11, color:"var(--text-muted)" }}>{c.pwa_role}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) return <div style={{ textAlign:"center", padding:40, color:"var(--text-muted)" }}>Chargement…</div>;
  if (!data)   return <div style={{ textAlign:"center", padding:40, color:"var(--text-muted)" }}>Portefeuille introuvable</div>;

  const { contacts, commercial, stats } = data;

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontWeight:800, fontSize:16 }}>💼 Portefeuille — {commercial?.name}</div>
          {commercial?.target_monthly > 0 && (
            <div style={{ fontSize:12, color:"var(--text-muted)", marginTop:4 }}>
              Objectif mensuel : <strong>{fmt(commercial.target_monthly)} €</strong>
            </div>
          )}
        </div>
        {isManager && <button className="btn sm" onClick={() => { setSelectedComm(""); setData(null); }}>← Changer de commercial</button>}
      </div>

      <div className="kpi-grid k4" style={{ marginBottom:20 }}>
        <div className="kpi"><div className="kpi-label">Contacts</div><div className="kpi-value">{stats.total}</div></div>
        <div className="kpi"><div className="kpi-label">CA gagné</div><div className="kpi-value" style={{ color:"var(--g)" }}>{fmt(stats.totalCa)} €</div></div>
        <div className="kpi"><div className="kpi-label">Pipeline pondéré</div><div className="kpi-value" style={{ color:"#8b5cf6" }}>{fmt(stats.totalPipe)} €</div></div>
        <div className="kpi"><div className="kpi-label">Jamais contactés</div><div className="kpi-value" style={{ color: stats.uncontacted > 0 ? "#e24b4a" : "var(--g)" }}>{stats.uncontacted}</div></div>
      </div>

      {stats.uncontacted > 0 && (
        <div style={{ padding:"8px 12px", background:"#e24b4a10", border:"1px solid #e24b4a30", borderRadius:8, marginBottom:12, fontSize:12, color:"#e24b4a" }}>
          ⚠️ {stats.uncontacted} contact(s) n'ont jamais eu d'activité — Pensez à les contacter !
        </div>
      )}

      <div className="card">
        <table className="tbl">
          <thead>
            <tr><th>Contact</th><th>Type</th><th>Statut</th><th>Activités</th><th>CA gagné</th><th>Pipeline</th><th>Dernière activité</th></tr>
          </thead>
          <tbody>
            {contacts.map(c => (
              <tr key={c.id} style={{ cursor:"pointer" }} onClick={() => setSelected(c)}>
                <td>
                  <div style={{ fontWeight:700 }}>{c.first_name} {c.last_name}</div>
                  <div style={{ fontSize:11, color:"var(--text-muted)" }}>{c.company_name}{c.city ? ` · ${c.city}` : ""}</div>
                </td>
                <td><span style={{ fontSize:11 }}>{TYPE_ICONS[c.type]} {c.type}</span></td>
                <td><span style={{ fontSize:11, padding:"2px 8px", borderRadius:8, background:`${STATUS_COLOR[c.status]}18`, color:STATUS_COLOR[c.status], fontWeight:700 }}>{c.status}</span></td>
                <td style={{ fontSize:12, textAlign:"center" }}>{c.activity_count}</td>
                <td style={{ fontSize:12, fontWeight:600, color:"var(--g)" }}>{c.ca_gagne > 0 ? `${fmt(c.ca_gagne)} €` : "—"}</td>
                <td style={{ fontSize:12, color:"#8b5cf6" }}>{c.pipeline_weighted > 0 ? `${fmt(c.pipeline_weighted)} €` : "—"}</td>
                <td style={{ fontSize:11, color: !c.last_activity_date ? "#e24b4a" : "var(--text-muted)" }}>
                  {c.last_activity_date ? fmtAgo(c.last_activity_date) : "jamais"}
                </td>
              </tr>
            ))}
            {contacts.length === 0 && <tr><td colSpan={7} style={{ textAlign:"center", padding:24, color:"var(--text-muted)" }}>Aucun contact dans ce portefeuille</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSelected(null)}>
          <div className="modal" style={{ maxWidth:420 }}>
            <div className="modal-header">
              <h3 className="modal-title">{TYPE_ICONS[selected.type]} {selected.first_name} {selected.last_name}</h3>
              <button className="modal-close" onClick={() => setSelected(null)}>✕</button>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              {[["Société", selected.company_name], ["Email", selected.email], ["Téléphone", selected.phone], ["Ville", selected.city], ["Statut", selected.status]].map(([l,v]) => v && (
                <div key={l} style={{ background:"#f9fafb", borderRadius:8, padding:"8px 10px" }}>
                  <div style={{ fontSize:10, color:"var(--text-muted)" }}>{l}</div>
                  <div style={{ fontSize:12, fontWeight:600 }}>{v}</div>
                </div>
              ))}
            </div>
            <div className="btn-group" style={{ justifyContent:"flex-end", marginTop:16 }}>
              <button className="btn" onClick={() => setSelected(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Onglet Équipe (manager/admin) ─────────────────────────────────
function EquipeTab({ currentCompanyId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [chartMetric, setChartMetric] = useState("ca_month");
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [unassigned, setUnassigned] = useState([]);
  const [commercials, setCommercials] = useState([]);

  const load = useCallback(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    api.crm.teamStats(currentCompanyId)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [currentCompanyId]);

  useEffect(() => { load(); }, [load]);

  const loadUnassigned = async () => {
    const rows = await api.crm.unassigned(currentCompanyId).catch(() => []);
    const comms = await api.crm.commercials().catch(() => []);
    setUnassigned(Array.isArray(rows) ? rows : []);
    setCommercials(comms);
    setShowUnassigned(true);
  };

  if (loading) return <div style={{ textAlign:"center", padding:40, color:"var(--text-muted)" }}>Chargement…</div>;
  if (!data)   return <div style={{ textAlign:"center", padding:40, color:"var(--text-muted)" }}>Données indisponibles</div>;

  const { commercials: team, inactiveAlerts, unassignedCount, caHistory } = data;

  const chartData = team.map((c, i) => ({
    name:       c.name.split(" ")[0],
    fullName:   c.name,
    ca_month:   Math.round(c.ca_month),
    ca_prev:    Math.round(c.ca_prev_month),
    pipeline:   Math.round(c.pipeline_weighted),
    nb_clients: c.nb_contacts,
    color:      BAR_COLORS[i % BAR_COLORS.length],
  }));

  const METRICS = [
    { key:"ca_month", label:"CA ce mois" },
    { key:"ca_prev",  label:"CA mois préc." },
    { key:"pipeline", label:"Pipeline pondéré" },
    { key:"nb_clients", label:"Nb clients" },
  ];

  return (
    <div>
      {/* Alertes */}
      {inactiveAlerts.length > 0 && (
        <div style={{ padding:"10px 14px", background:"#e24b4a10", border:"1px solid #e24b4a30", borderRadius:8, marginBottom:16, fontSize:12, color:"#e24b4a" }}>
          ⚠️ <strong>{inactiveAlerts.length} commercial(aux)</strong> sans activité depuis +30 jours :
          {inactiveAlerts.map(a => <span key={a.id} style={{ marginLeft:8, fontWeight:600 }}>{a.name} ({a.days_inactive}j)</span>)}
        </div>
      )}
      {unassignedCount > 0 && (
        <div style={{ padding:"10px 14px", background:"#ba751710", border:"1px solid #ba751730", borderRadius:8, marginBottom:16, fontSize:12, color:"#ba7517", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span>📋 <strong>{unassignedCount} contact(s)</strong> sans commercial assigné</span>
          <button className="btn sm" onClick={loadUnassigned}>Affecter →</button>
        </div>
      )}

      {/* Graphique */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
          <div style={{ fontWeight:700, fontSize:13 }}>Comparatif commerciaux</div>
          <div style={{ display:"flex", gap:6 }}>
            {METRICS.map(m => (
              <button key={m.key} className={`btn sm ${chartMetric === m.key ? "primary" : ""}`} onClick={() => setChartMetric(m.key)} style={{ fontSize:11 }}>{m.label}</button>
            ))}
          </div>
        </div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top:0, right:0, left:0, bottom:0 }}>
            <XAxis dataKey="name" tick={{ fontSize:11 }} />
            <YAxis tick={{ fontSize:10 }} tickFormatter={v => v > 999 ? `${(v/1000).toFixed(0)}k` : v} />
            <Tooltip formatter={v => chartMetric.startsWith("ca") || chartMetric === "pipeline" ? [`${fmt(v)} €`] : [v]} labelFormatter={l => chartData.find(d => d.name === l)?.fullName || l} />
            <Bar dataKey={chartMetric} radius={[4,4,0,0]}>
              {chartData.map((d, i) => <Cell key={i} fill={d.color} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tableau comparatif */}
      <div className="card">
        <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Classement mensuel</div>
        <table className="tbl">
          <thead>
            <tr>
              <th>#</th><th>Commercial</th><th>Nb clients</th><th>CA mois</th><th>CA préc.</th>
              <th>Évolution</th><th>Cmdes</th><th>Panier moy.</th><th>Pipeline</th><th>Dernière activité</th>
            </tr>
          </thead>
          <tbody>
            {team.map((c, i) => {
              const evol = c.ca_prev_month > 0 ? ((c.ca_month - c.ca_prev_month) / c.ca_prev_month * 100) : null;
              const avgBasket = c.orders_month > 0 ? c.ca_month / c.orders_month : 0;
              const daysInactive = c.last_activity ? Math.floor((Date.now() - new Date(c.last_activity)) / 86400000) : 999;
              return (
                <tr key={c.id}>
                  <td style={{ fontWeight:700, color:BAR_COLORS[i % BAR_COLORS.length], fontSize:16 }}>{i+1}</td>
                  <td>
                    <div style={{ fontWeight:700 }}>{c.name}</div>
                    <div style={{ fontSize:10, color:"var(--text-muted)" }}>{c.pwa_role}</div>
                    {c.target_monthly > 0 && (
                      <div style={{ marginTop:4 }}>
                        <div style={{ height:4, background:"#e2e8f0", borderRadius:2, width:80 }}>
                          <div style={{ height:"100%", background:"var(--g)", borderRadius:2, width:`${Math.min(100, c.ca_month/c.target_monthly*100)}%` }} />
                        </div>
                        <div style={{ fontSize:10, color:"var(--text-muted)" }}>{Math.round(c.ca_month/c.target_monthly*100)}% obj.</div>
                      </div>
                    )}
                  </td>
                  <td style={{ textAlign:"center", fontWeight:600 }}>{c.nb_contacts}</td>
                  <td style={{ fontWeight:700, color:"var(--g)" }}>{fmt(c.ca_month)} €</td>
                  <td style={{ fontSize:12, color:"var(--text-muted)" }}>{fmt(c.ca_prev_month)} €</td>
                  <td>
                    {evol !== null ? (
                      <span style={{ fontSize:12, fontWeight:700, color: evol >= 0 ? "#1a9e72" : "#e24b4a" }}>
                        {evol >= 0 ? "▲" : "▼"} {Math.abs(evol).toFixed(1)}%
                      </span>
                    ) : <span style={{ color:"var(--text-muted)", fontSize:11 }}>—</span>}
                  </td>
                  <td style={{ textAlign:"center" }}>{c.orders_month}</td>
                  <td style={{ fontSize:12, color:"#185fa5" }}>{avgBasket > 0 ? `${fmt(avgBasket)} €` : "—"}</td>
                  <td style={{ fontSize:12, color:"#8b5cf6" }}>{fmt(c.pipeline_weighted)} €</td>
                  <td>
                    <span style={{ fontSize:11, color: daysInactive > 30 ? "#e24b4a" : daysInactive > 14 ? "#ba7517" : "#1a9e72" }}>
                      {daysInactive >= 999 ? "jamais" : daysInactive === 0 ? "aujourd'hui" : `il y a ${daysInactive}j`}
                    </span>
                  </td>
                </tr>
              );
            })}
            {team.length === 0 && <tr><td colSpan={10} style={{ textAlign:"center", padding:24, color:"var(--text-muted)" }}>Aucun commercial configuré</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Modal contacts non assignés */}
      {showUnassigned && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowUnassigned(false)}>
          <div className="modal" style={{ maxWidth:620 }}>
            <div className="modal-header">
              <h3 className="modal-title">📋 Contacts sans commercial ({unassigned.length})</h3>
              <button className="modal-close" onClick={() => setShowUnassigned(false)}>✕</button>
            </div>
            <table className="tbl">
              <thead><tr><th>Contact</th><th>Type</th><th>Créé le</th><th>Affecter à</th></tr></thead>
              <tbody>
                {unassigned.map(c => (
                  <tr key={c.id}>
                    <td>
                      <div style={{ fontWeight:600 }}>{c.first_name} {c.last_name}</div>
                      <div style={{ fontSize:11, color:"var(--text-muted)" }}>{c.company_name}</div>
                    </td>
                    <td><span style={{ fontSize:11 }}>{TYPE_ICONS[c.type]} {c.type}</span></td>
                    <td style={{ fontSize:11 }}>{fmtDate(c.created_at)}</td>
                    <td>
                      <select className="form-control" style={{ width:150 }}
                        defaultValue=""
                        onChange={async (e) => {
                          if (!e.target.value) return;
                          await api.crm.assignContact(c.id, { commercial_id: e.target.value }).catch(console.error);
                          setUnassigned(p => p.filter(x => x.id !== c.id));
                          setData(d => d ? { ...d, unassignedCount: d.unassignedCount - 1 } : d);
                        }}>
                        <option value="">— Choisir —</option>
                        {commercials.map(cm => <option key={cm.id} value={cm.id}>{cm.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                {unassigned.length === 0 && <tr><td colSpan={4} style={{ textAlign:"center", padding:24, color:"var(--text-muted)" }}>Tous les contacts sont assignés !</td></tr>}
              </tbody>
            </table>
            <div className="btn-group" style={{ justifyContent:"flex-end", marginTop:12 }}>
              <button className="btn" onClick={() => { setShowUnassigned(false); load(); }}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Onglet Rapports ───────────────────────────────────────────────
function ReportsTab({ currentCompanyId }) {
  const [reports, setReports] = useState(null);

  useEffect(() => {
    if (!currentCompanyId) return;
    api.crm.reports(currentCompanyId).then(setReports).catch(console.error);
  }, [currentCompanyId]);

  if (!reports) return <div style={{ textAlign:"center", padding:40, color:"var(--text-muted)" }}>Chargement…</div>;

  return (
    <div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        <div className="card">
          <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Sources de leads</div>
          {reports.bySource.length === 0 ? <div style={{ color:"var(--text-muted)", fontSize:12 }}>Aucune donnée</div> : reports.bySource.map(s => (
            <div key={s.source} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:600 }}>{s.source}</div>
                <div style={{ height:6, background:"#e2e8f0", borderRadius:4, marginTop:4 }}>
                  <div style={{ height:"100%", background:"var(--g)", borderRadius:4, width:`${Math.min(100, (s.count/Math.max(...reports.bySource.map(x=>x.count)))*100)}%` }} />
                </div>
              </div>
              <div style={{ textAlign:"right", fontSize:11 }}>
                <div style={{ fontWeight:700 }}>{s.count}</div>
                <div style={{ color:"#1a9e72" }}>{s.won} gagnés</div>
              </div>
            </div>
          ))}
        </div>

        <div className="card">
          <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Performance par commercial</div>
          {reports.byAssignee.length === 0 ? <div style={{ color:"var(--text-muted)", fontSize:12 }}>Aucune donnée</div> : reports.byAssignee.map((a, i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 0", borderBottom:"var(--border)" }}>
              <div style={{ width:28, height:28, borderRadius:"50%", background:"var(--g)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:700 }}>{a.assigned_to?.[0]?.toUpperCase() || "?"}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:12, fontWeight:600 }}>{a.assigned_to || "Non assigné"}</div>
                <div style={{ fontSize:11, color:"var(--text-muted)" }}>{a.contacts} contacts</div>
              </div>
              <div style={{ fontSize:12, fontWeight:700, color:"var(--g)" }}>{fmt(a.pipeline)} €</div>
            </div>
          ))}
        </div>

        <div className="card">
          <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Durée moyenne du cycle de vente</div>
          <div style={{ textAlign:"center", padding:20 }}>
            <div style={{ fontSize:36, fontWeight:700, color:"var(--g)" }}>{reports.avgCycleDays}</div>
            <div style={{ fontSize:13, color:"var(--text-muted)" }}>jours en moyenne</div>
          </div>
        </div>

        <div className="card">
          <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>Évolution mensuelle</div>
          {reports.monthly.slice(0, 6).map(m => (
            <div key={m.month} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
              <span style={{ fontSize:11, color:"var(--text-muted)", width:60 }}>{m.month}</span>
              <div style={{ flex:1, height:6, background:"#e2e8f0", borderRadius:4 }}>
                <div style={{ height:"100%", background:"var(--g)", borderRadius:4, width:`${Math.min(100, (m.contacts/Math.max(1,...reports.monthly.map(x=>x.contacts)))*100)}%` }} />
              </div>
              <span style={{ fontSize:11, fontWeight:700, width:30 }}>{m.contacts}</span>
              <span style={{ fontSize:11, color:"#1a9e72", width:30 }}>{m.won} ✓</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Page principale CRM ───────────────────────────────────────────
export default function CRM({ currentCompanyId }) {
  const { user } = useAuth();
  const [tab, setTab] = useState("dashboard");

  const isManager = user?.role === 'admin' || user?.pwa_role === 'manager';

  const ALL_TABS = [
    { id: "dashboard",    label: "Tableau de bord",     icon: "📊" },
    { id: "contacts",     label: "Contacts & Prospects", icon: "👥" },
    { id: "pipeline",     label: "Pipeline Kanban",      icon: "🏗" },
    { id: "activities",   label: "Activités & Agenda",   icon: "📅" },
    { id: "portefeuille", label: "Mon Portefeuille",      icon: "💼" },
    ...(isManager ? [{ id: "equipe", label: "Équipe Commerciale", icon: "🏆" }] : []),
    { id: "reports",      label: "Rapports",             icon: "📈" },
  ];

  if (!currentCompanyId) return (
    <div style={{ textAlign:"center", padding:80, color:"var(--text-muted)" }}>
      <div style={{ fontSize:48, marginBottom:16 }}>🤝</div>
      <div style={{ fontSize:16, fontWeight:600 }}>Sélectionnez une société pour accéder au CRM</div>
    </div>
  );

  return (
    <div>
      <div className="page-header" style={{ marginBottom:0 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <h2 className="page-title" style={{ marginBottom:2 }}>CRM — Gestion commerciale B2B</h2>
          <NotifBell currentCompanyId={currentCompanyId} />
        </div>
      </div>

      <div style={{ display:"flex", gap:2, borderBottom:"2px solid var(--border-color,#e2e8f0)", marginBottom:20, marginTop:16, overflowX:"auto" }}>
        {ALL_TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding:"9px 16px", fontSize:13, fontWeight:tab===t.id?700:500, cursor:"pointer", border:"none", background:"transparent", color:tab===t.id?"var(--g)":"var(--text-muted)", borderBottom:`2px solid ${tab===t.id?"var(--g)":"transparent"}`, marginBottom:-2, whiteSpace:"nowrap", display:"flex", alignItems:"center", gap:6 }}>
            <span>{t.icon}</span><span>{t.label}</span>
          </button>
        ))}
      </div>

      {tab === "dashboard"    && <DashboardCRM    currentCompanyId={currentCompanyId} user={user} />}
      {tab === "contacts"     && <ContactsTab     currentCompanyId={currentCompanyId} user={user} />}
      {tab === "pipeline"     && <PipelineTab     currentCompanyId={currentCompanyId} />}
      {tab === "activities"   && <ActivitiesTab   currentCompanyId={currentCompanyId} />}
      {tab === "portefeuille" && <PortefeuilleTab currentCompanyId={currentCompanyId} user={user} />}
      {tab === "equipe"       && isManager && <EquipeTab currentCompanyId={currentCompanyId} />}
      {tab === "reports"      && <ReportsTab      currentCompanyId={currentCompanyId} />}
    </div>
  );
}
